import React, { useEffect, useState } from 'react';
import Page from '../Page';
import Config from '@go-client/lib/config';

import {
  AvailableFiltersOptions,
  FilterActionType,
  GroupActionType,
  Sorting,
  SortOrder,
  UserAction,
  UserActionRecordDetailed,
  UserActionRecordPagination,
} from '@go-shared/types/userActionRecord';
import { getFilteredActions } from '@go-client/lib/api/usersActions';
import { UserRecords } from '@go-client/components/UserRecords/UserRecords';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';
// tslint:disable-next-line:max-line-length
import { UserActionsHeader } from '@go-client/components/UserRecords/UserActionsHeader';
import './UserActions.scss';
import { MenuBar } from '@go-client/components';
import { RouteComponentProps } from 'react-router-dom';
import PageWrapper from '../PageWrapper';

import { BubbleTypeaheadValue } from '@go-client/components/BubbleUI/BubbleTypeahead';
import { addDaysToUTCDate, isoDate, today } from '@go-shared/lib/time';
import { UserActionsSidebar } from '@go-client/components/UserRecords/UserActionsSidebar';
import { getSimpleStaffTemplates } from '@go-client/lib/api/staffTemplate';
import { BareStaffTemplate } from '@go-shared/types/staffTemplate';
import { BareTemplate } from '@go-shared/types/template';
import { getSimpleDayTemplates } from '@go-client/lib/api/template';
import { BareSimpleGroupBooking } from '@go-shared/types/groupBooking';
import { getSimpleGroupBookingsByProperty } from '@go-client/lib/api/groupBooking';

export type UserActionRecordUIFilters = {
  users: Array<BubbleTypeaheadValue<string>>;
  from: string;
  to: string;
  action: FilterActionType;
  subAction: UserAction;
  groupAction: GroupActionType;
  page: number;
  propertyId: string;
  venueTemplateId: string[];
  eventTemplateId: string[];
  contentTemplateId: string[];
  staffTemplateId: Array<BubbleTypeaheadValue<string>>;
  templateId: Array<BubbleTypeaheadValue<string>>;
  groupBookingId: Array<BubbleTypeaheadValue<Pick<BareSimpleGroupBooking, '_id' | 'party'>>>;
  groupBookingPartyId: Array<string>;
  archived: boolean | undefined;
  locked: boolean | undefined;
  sort: Sorting;

  refersId: Array<string>;

  refersEventTemplateId: string[];
  refersVenueTemplateId: string[];
  refersContentTemplateId: string[];
  segments: Array<BubbleTypeaheadValue<string>>;
  levels: Array<BubbleTypeaheadValue<string>>;
  attributes: Array<BubbleTypeaheadValue<string>>;
  venueTypes: Array<BubbleTypeaheadValue<string>>;
  mealPeriods: Array<BubbleTypeaheadValue<string>>;
  dayThemes: Array<BubbleTypeaheadValue<string>>;
  highlights: Array<BubbleTypeaheadValue<string>>;
  commonTexts: Array<BubbleTypeaheadValue<string>>;
};

export type FilterOptions = AvailableFiltersOptions & {
  staffTemplates: Pick<BareStaffTemplate, '_id' | 'title'>[];
  templates: Pick<BareTemplate, '_id' | 'name'>[];
  groupBookings: Pick<BareSimpleGroupBooking, '_id' | 'party'>[];
};

const UsersActions: React.FC<RouteComponentProps> = props => {
  const [userRecords, setUserRecords] = useState<UserActionRecordDetailed[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState<UserActionRecordPagination | undefined>(undefined);
  const [availableFiltersOptions, setAvailableFiltersOptions] = useState<FilterOptions>(
    {} as FilterOptions
  );

  const [filters, setFilters] = useState<UserActionRecordUIFilters>(() => ({
    users: [],
    from: isoDate(addDaysToUTCDate(new Date(today()), -7)),
    to: today(),
    action: 'ALL',
    subAction: '',
    groupAction: 'ALL',
    page: 1,
    propertyId: '',
    venueTemplateId: [],
    eventTemplateId: [],
    contentTemplateId: [],
    staffTemplateId: [],
    templateId: [],
    translationId: [],
    archived: undefined,
    locked: undefined,
    groupBookingId: [],
    groupBookingPartyId: [],
    sort: { createdAt: SortOrder.DESC },
    refersId: [],
    refersEventTemplateId: [],
    refersVenueTemplateId: [],
    refersContentTemplateId: [],
    segments: [],
    levels: [],
    attributes: [],
    venueTypes: [],
    mealPeriods: [],
    dayThemes: [],
    highlights: [],
    commonTexts: [],
  }));

  useEffect(() => {
    getFilteredRecords(filters).then().catch(console.error);
  }, [filters]);

  const getFilteredRecords = async (filters: UserActionRecordUIFilters) => {
    setIsLoading(true);

    try {
      const { records, pagination, availableFilters } = await getFilteredActions({
        ...filters,
        users: filters.users.map(user => user.refKey),
        venueTemplateId: filters.venueTemplateId,
        eventTemplateId: filters.eventTemplateId,
        contentTemplateId: filters.contentTemplateId,
        staffTemplateId: filters.staffTemplateId.map(st => st.refKey),
        templateId: filters.templateId.map(vt => vt.refKey),
        refersId: [
          ...filters.refersEventTemplateId,
          ...filters.refersVenueTemplateId,
          ...filters.refersContentTemplateId,
          ...filters.segments.map(l => l.refKey),
          ...filters.levels.map(l => l.refKey),
          ...filters.attributes.map(l => l.refKey),
          ...filters.venueTypes.map(l => l.refKey),
          ...filters.mealPeriods.map(l => l.refKey),
          ...filters.dayThemes.map(l => l.refKey),
          ...filters.highlights.map(l => l.refKey),
          ...filters.commonTexts.map(l => l.refKey),
        ],
        groupBookingId: filters.groupBookingId.map(t => t.refKey),
        groupBookingPartyId: filters.groupBookingPartyId.map(t => t),
        sort: filters.sort,
      });

      const results = await Promise.all([
        getSimpleStaffTemplates(),
        filters.propertyId ? getSimpleDayTemplates(filters.propertyId) : [],
        filters.propertyId ? getSimpleGroupBookingsByProperty(filters.propertyId) : [],
      ]);

      const [staffTemplates, templates, groupBookings] = results;
      setUserRecords(records);
      setPagination(pagination);
      setAvailableFiltersOptions({
        ...availableFilters,
        staffTemplates,
        templates,
        groupBookings,
      });
    } catch (e) {
      setUserRecords([]);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <PageWrapper {...props}>
      <Page className="Groups absolute flex items-stretch">
        <MenuBar {...props} />
        <div className="SideMenu bg-blue-darkest flex flex-col">
          <div className="bg-white flex flex-col items-center">
            <BrandLogo logo={Config.BRAND_LOGO_URL} />
          </div>

          <UserActionsSidebar
            onFiltersChange={setFilters}
            activeFilters={filters}
            availableFiltersOptions={availableFiltersOptions}
          />
        </div>

        <div className="UserActionsContent flex-grow bg-blue-lightest flex flex-col overflow-y-auto">
          <UserActionsHeader
            pagination={pagination}
            activeFilters={filters}
            onFiltersChange={setFilters}
          />

          <UserRecords
            records={userRecords}
            loading={isLoading}
            activeFilters={filters}
            onFiltersChange={setFilters}
          />
        </div>
      </Page>
    </PageWrapper>
  );
};

export default UsersActions;
